/** 测距 / 面积标注 Marker 绘制（marker 列表由调用方持有） */
import mapboxgl from 'mapbox-gl';
import { toMapLngLat } from '../dashboard/basemapCoord';
import {
  dedupeConsecutiveCoords,
  haversineMeters,
  pathLengthMeters,
  polygonAreaMeters,
  formatDistance,
  formatArea,
  openRingCoords,
  ringCentroid,
  rectangleRingFromCorners
} from './geometry';

export function clearMeasureMarkers(markersRef) {
  (markersRef.current || []).forEach((m) => {
    try { m.remove(); } catch (_) { /* ignore */ }
  });
  markersRef.current = [];
}

export function addMeasureLabel(map, markersRef, lngLat, text, variant = 'seg') {
  if (!map || !lngLat) return;
  const el = document.createElement('div');
  const variantClass =
    variant === 'total' ? ' is-total' : variant === 'area' ? ' is-area' : '';
  el.className = `zhxt-draw-measure-label${variantClass}`;
  el.textContent = text;
  const marker = new mapboxgl.Marker({
    element: el,
    anchor: variant === 'total' ? 'bottom' : 'center',
    offset: variant === 'total' ? [0, -10] : [0, 0]
  })
    .setLngLat(lngLat)
    .addTo(map);
  markersRef.current.push(marker);
}

/** 测距：边长 + 总长；区域/矩形：各边长 + 框内面积 */
export function refreshMeasureLabels(map, markersRef, list, draftPts, draftKind) {
  clearMeasureMarkers(markersRef);
  if (!map) return;

  const paintLineLabels = (coords, showTotal = true) => {
    const clean = dedupeConsecutiveCoords(coords, 0.5);
    if (clean.length < 2) return;
    for (let i = 1; i < clean.length; i += 1) {
      const a = clean[i - 1];
      const b = clean[i];
      const seg = haversineMeters(
        { lng: a[0], lat: a[1] },
        { lng: b[0], lat: b[1] }
      );
      if (seg < 0.5) continue;
      const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      addMeasureLabel(map, markersRef, toMapLngLat(mid[0], mid[1]) || mid, formatDistance(seg), 'seg');
    }
    if (showTotal) {
      const total = pathLengthMeters(clean);
      if (total < 0.5) return;
      const last = clean[clean.length - 1];
      addMeasureLabel(map, markersRef, toMapLngLat(last[0], last[1]) || last, `总长 ${formatDistance(total)}`, 'total');
    }
  };

  const paintPolygonLabels = (coords) => {
    const ring = openRingCoords(coords);
    if (ring.length < 3) return;
    const closed = [...ring, [ring[0][0], ring[0][1]]];
    for (let i = 1; i < closed.length; i += 1) {
      const a = closed[i - 1];
      const b = closed[i];
      const seg = haversineMeters(
        { lng: a[0], lat: a[1] },
        { lng: b[0], lat: b[1] }
      );
      if (seg < 0.5) continue;
      const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      const midDisplay = toMapLngLat(mid[0], mid[1]) || mid;
      addMeasureLabel(map, markersRef, midDisplay, formatDistance(seg), 'seg');
    }
    const area = polygonAreaMeters(ring);
    if (area < 1) return;
    const center = ringCentroid(ring);
    if (center) {
      const centerDisplay = toMapLngLat(center[0], center[1]) || center;
      addMeasureLabel(map, markersRef, centerDisplay, `面积 ${formatArea(area)}`, 'area');
    }
  };

  (list || []).forEach((f) => {
    if (f.type === 'measure' && f.coordinates && f.coordinates.length >= 2) {
      paintLineLabels(f.coordinates, true);
    }
    if (
      (f.type === 'polygon' || f.type === 'rectangle') &&
      f.coordinates &&
      f.coordinates.length >= 3
    ) {
      paintPolygonLabels(f.coordinates);
    }
  });

  if (draftKind === 'measure' && draftPts && draftPts.length >= 2) {
    paintLineLabels(draftPts, true);
  }
  if (draftKind === 'polygon' && draftPts) {
    if (draftPts.length >= 3) paintPolygonLabels(draftPts);
    else if (draftPts.length >= 2) paintLineLabels(draftPts, false);
  }
  if (draftKind === 'rectangle' && draftPts && draftPts.length >= 2) {
    paintPolygonLabels(rectangleRingFromCorners(draftPts[0], draftPts[1]));
  }
}
